/**
 * @module Library
 *
 */
import React from "react";
import bs from "@bugsnag/js";
import bsr from "@bugsnag/plugin-react";
import { Bugsnag } from "@bugsnag/browser";
import { UserModel } from "app/models";
import { featureIsEnabled } from "app/utils/features";
import config from "config/environment";
import { ErrorFallback } from "app/components/helpers/ErrorFallback";

interface BugsnagUser {
  id: string;
  name?: string;
  email?: string;
}

let bugsnag: Partial<Bugsnag.Client> = {
  notify: () => false,
};

// generate a unique error ID
function generateErrorId(): string {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 9);
  return `${timestamp}-${randomStr}`.toUpperCase();
}

// Store the last errorId and timestamp so related errors share the same ID
let lastErrorId: string | null = null;
let lastErrorTime: number = 0;
const ERROR_SESSION_TIMEOUT = 5000; // 5 seconds - errors within this window share the same ID

// Get or create errorId for current error session
function getErrorId(): string {
  const now = Date.now();

  // If we have a recent errorId (within timeout), reuse it
  if (lastErrorId && now - lastErrorTime < ERROR_SESSION_TIMEOUT) {
    return lastErrorId;
  }

  // Generate new errorId for new error session
  lastErrorId = generateErrorId();
  lastErrorTime = now;
  return lastErrorId;
}

// DevErrorBoundary for when Bugsnag is disabled
class DevErrorBoundary extends React.Component<
  React.PropsWithChildren<{}>,
  { hasError: boolean; errorId?: string }
> {
  constructor(props: React.PropsWithChildren<{}>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean; errorId: string } {
    return { hasError: true, errorId: generateErrorId() };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    console.error("Error ID:", this.state.errorId);
  }

  render() {
    if (this.state.hasError) {
      return React.createElement(ErrorFallback, { errorId: this.state.errorId });
    }
    return this.props.children;
  }
}

let ErrorBoundary: React.ComponentType<React.PropsWithChildren<{}>> = DevErrorBoundary;

if (featureIsEnabled("bugsnagTool")) {
  bugsnag = bs({
    apiKey: config.BUGSNAG_API_KEY,
    appVersion: config.VERSION,
    releaseStage: config.ENVIRONMENT,
    // Global beforeSend handler - adds errorId to all errors (v6 API)
    beforeSend: (report: any) => {
      const errorId = getErrorId();

      // Add errorId to the error message for easier searching
      report.errorMessage = `[${errorId}] ${report.errorMessage}`;

      // Add error tracking metadata
      report.updateMetaData("errorTracking", {
        errorId,
        userId: (bugsnag.user as BugsnagUser)?.id || "unknown",
      });
    },
  });

  bugsnag.use(bsr, React);

  // Use Bugsnag's built-in ErrorBoundary for proper source map handling
  const BugsnagErrorBoundary = bugsnag.getPlugin("react");

  // FallbackComponent that displays the errorId
  const BugsnagFallback: React.FC<{ error: Error }> = () => {
    const [errorId] = React.useState(() => getErrorId());
    return React.createElement(ErrorFallback, { errorId });
  };

  // Wrapper that shows custom fallback
  ErrorBoundary = ({ children }: React.PropsWithChildren<{}>) =>
    React.createElement(
      BugsnagErrorBoundary,
      { FallbackComponent: BugsnagFallback },
      children
    );
}

export { bugsnag, ErrorBoundary };

export function bugsnagAddUser(user: UserModel) {
  if (user != null) {
    bugsnag.user = {
      id: user.id,
      name: user.name,
      email: user.email,
    };

    if (bugsnag.metaData == null) {
      bugsnag.metaData = {};
    }

    (bugsnag.metaData as Record<string, unknown>).user = {
      kind: user.kind,
      customerType: user.customerType,
      hasActiveSubscription: user.hasActiveSubscription,
      hasBillingInfo: user.hasBillingInfo,
      hasCoachPlusSubscription: user.hasCoachPlusSubscription,
      hasTrialSubscription: user.hasTrialSubscription,
      needsOnboarding: user.needsOnboarding,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
    };
  }
}
